import { parseNumericInput } from './numberInput';

/** @type {string} */
export const QUOTE_FUNDING_REQUIRED_MESSAGE =
  'Either a budget or a supplier credit with a calculable amount is required.';

/**
 * @param {*} price
 * @param {*} discount
 * @returns {number}
 */
function unitPriceAfterDiscount(price, discount) {
  const priceNum = parseFloat(price);
  if (!Number.isFinite(priceNum)) {
    return NaN;
  }
  const discountPct = parseFloat(discount);
  const discountAmount = Number.isFinite(discountPct)
    ? (priceNum * discountPct) / 100
    : 0;
  return priceNum - discountAmount;
}

/**
 * Sum quote line totals for supplier-credit funding_amount (qty × unit after discount).
 *
 * @param {Array<{ quantity?: *, price?: *, discount?: * }>} items
 * @returns {number}
 */
export function sumQuoteLineFundingAmount(items) {
  return (Array.isArray(items) ? items : []).reduce((sum, item) => {
    const qty = parseNumericInput(item?.quantity, { integerOnly: true });
    if (qty == null || qty < 1) {
      return sum;
    }
    const pad =
      item?.price_after_discount != null &&
      String(item.price_after_discount).trim() !== ''
        ? parseNumericInput(item.price_after_discount)
        : null;
    const unitPrice =
      pad != null && Number.isFinite(pad)
        ? pad
        : unitPriceAfterDiscount(item?.price ?? '', item?.discount ?? '');
    if (!Number.isFinite(unitPrice)) {
      return sum;
    }
    return sum + qty * unitPrice;
  }, 0);
}

/**
 * Resolve numeric funding amount for supplier-credit path.
 *
 * @param {Object} params
 * @param {Array} [params.items]
 * @param {boolean} [params.isPrepayment]
 * @param {*} [params.prepaymentAmount]
 * @returns {number}
 */
export function resolveQuoteFundingAmount({
  items = [],
  isPrepayment = false,
  prepaymentAmount = null,
} = {}) {
  if (isPrepayment) {
    const parsed = parseNumericInput(prepaymentAmount);
    return parsed != null && parsed > 0 ? parsed : 0;
  }
  const total = sumQuoteLineFundingAmount(items);
  return Number.isFinite(total) && total > 0 ? Math.round(total * 100) / 100 : 0;
}

/**
 * Whether supplier-credit funding has a positive calculable amount.
 *
 * @param {Object} params
 * @param {*} params.fundingCreditId
 * @param {Array} [params.items]
 * @param {boolean} [params.isPrepayment]
 * @param {*} [params.prepaymentAmount]
 * @returns {boolean}
 */
export function hasValidQuoteCreditFunding({
  fundingCreditId,
  items = [],
  isPrepayment = false,
  prepaymentAmount = null,
} = {}) {
  const hasCredit =
    fundingCreditId != null && String(fundingCreditId).trim() !== '';
  if (!hasCredit) {
    return false;
  }
  return resolveQuoteFundingAmount({ items, isPrepayment, prepaymentAmount }) > 0;
}

/**
 * Build API funding fields with mutual exclusivity (budget wins when both set).
 * Preferred write key: `budget_id` (legacy `budget_line_id` accepted by backend).
 *
 * @param {Object} params
 * @param {string|number|null} [params.budgetId] - preferred
 * @param {string|number|null} [params.budgetLineId] - alias for budgetId
 * @param {string} [params.budget]
 * @param {string|number|null} [params.fundingCreditId]
 * @param {Array} [params.items]
 * @param {boolean} [params.isPrepayment]
 * @param {*} [params.prepaymentAmount]
 * @returns {{ ok: true, fields: Object } | { ok: false, error: string }}
 */
export function buildQuoteFundingFields({
  budgetId = null,
  budgetLineId = null,
  budget = '',
  fundingCreditId = null,
  items = [],
  isPrepayment = false,
  prepaymentAmount = null,
} = {}) {
  const resolvedBudgetId =
    budgetId != null && String(budgetId).trim() !== ''
      ? budgetId
      : budgetLineId != null && String(budgetLineId).trim() !== ''
        ? budgetLineId
        : null;

  if (resolvedBudgetId != null) {
    return {
      ok: true,
      fields: {
        budget_id: resolvedBudgetId,
        budget: null,
        funding_credit_id: null,
        funding_amount: null,
      },
    };
  }

  const budgetText = budget != null ? String(budget).trim() : '';

  if (budgetText !== '') {
    return {
      ok: true,
      fields: {
        budget: budgetText,
        budget_id: null,
        funding_credit_id: null,
        funding_amount: null,
      },
    };
  }

  const creditId =
    fundingCreditId != null && String(fundingCreditId).trim() !== ''
      ? fundingCreditId
      : null;

  if (creditId != null) {
    const amount = resolveQuoteFundingAmount({
      items,
      isPrepayment,
      prepaymentAmount,
    });
    if (amount <= 0) {
      return {
        ok: false,
        error: isPrepayment
          ? 'Enter a prepayment amount when using a supplier credit.'
          : 'Add line items with quantity and price when using a supplier credit.',
      };
    }
    return {
      ok: true,
      fields: {
        budget: null,
        budget_id: null,
        funding_credit_id: creditId,
        funding_amount: String(amount),
      },
    };
  }

  return { ok: false, error: QUOTE_FUNDING_REQUIRED_MESSAGE };
}

/**
 * Extract user-facing message from quote API error payload.
 *
 * @param {*} errorPayload
 * @returns {string|null}
 */
export function formatQuoteApiErrorMessage(errorPayload) {
  if (!errorPayload) {
    return null;
  }
  if (typeof errorPayload === 'string') {
    return errorPayload;
  }
  if (Array.isArray(errorPayload)) {
    const parts = errorPayload.filter(Boolean).map(String);
    return parts.length ? parts.join(' ') : null;
  }
  if (Array.isArray(errorPayload.non_field_errors)) {
    const parts = errorPayload.non_field_errors.filter(Boolean).map(String);
    if (parts.length) {
      return parts.join(' ');
    }
  }
  if (errorPayload.detail) {
    return String(errorPayload.detail);
  }
  return null;
}
